import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  isDevMode,
  importProvidersFrom,
} from '@angular/core';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { ActivatedRouteSnapshot, NavigationEnd, provideRouter, RouteReuseStrategy, Router } from '@angular/router';
import { filter } from 'rxjs';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { LayoutRouteReuseStrategy } from './layout/layout-route-reuse.strategy';
import { provideKeycloak } from 'keycloak-angular';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

// Do not override `colorScheme.dark.surface` with a light slate scale — that breaks
// PrimeNG dark tokens (inputs, menus, overlays) which reference surface.* indices.
const JRetailPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
  },
});
import { TranslateModule, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import {
  tokenInterceptor,
  deviceIdInterceptor,
  workingPeriodInterceptor,
  unauthorizedInterceptor,
} from '@auth/interceptors';
import { apiErrorMessageInterceptor, fuelManagementInterceptor } from '@core/interceptors';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@env/environment';
import { isMixpanelEnabled } from './analytics/mixpanel-bootstrap';
import { AnalyticsService } from '@core/services/analytics.service';
import { isSentryEnabled } from './sentry/sentry-bootstrap';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

const realm = localStorage.getItem('realm') ?? '';

const sentryProviders = isSentryEnabled(environment)
  ? [
      { provide: ErrorHandler, useValue: createErrorHandler({ logErrors: !environment.production }) },
      provideAppInitializer(() => {
        inject(TraceService);
      }),
    ]
  : [];

function getDeepestRouteData(snapshot: ActivatedRouteSnapshot): Record<string, string> {
  let current = snapshot;
  while (current.firstChild) {
    current = current.firstChild;
  }
  const data: Record<string, string> = {};
  for (const [key, value] of Object.entries(current.data)) {
    if (typeof value === 'string') {
      data[key] = value;
    }
  }
  return data;
}

const mixpanelProviders = isMixpanelEnabled(environment)
  ? [
      provideAppInitializer(() => {
        const router = inject(Router);
        const analytics = inject(AnalyticsService);

        router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
          const nav = event as NavigationEnd;
          const routeData = getDeepestRouteData(router.routerState.snapshot.root);
          const properties: Record<string, string> = {};
          if (routeData['tabTitle']) {
            properties['tab_title'] = routeData['tabTitle'];
          }
          analytics.trackPageView(nav.urlAfterRedirects, properties);
        });
      }),
    ]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    ...sentryProviders,
    ...mixpanelProviders,
    providePrimeNG({
      overlayAppendTo: 'body',
      theme: {
        preset: JRetailPreset,
        options: {
          darkModeSelector: '.theme-dark',
        },
      },
    }),
    provideHttpClient(
      withInterceptors([
        deviceIdInterceptor,
        workingPeriodInterceptor,
        tokenInterceptor,
        apiErrorMessageInterceptor,
        fuelManagementInterceptor,
        unauthorizedInterceptor,
      ])
    ),
    importProvidersFrom(TranslateModule),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      lang: 'tr',
      fallbackLang: 'en',
    }),
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm,
        clientId: environment.keycloakClientId,
      },
      ...(realm
        ? {
            initOptions: {
              onLoad: 'check-sso',
              silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
              checkLoginIframe: false,
            },
          }
        : {}),
    }),
    provideRouter(routes),
    { provide: RouteReuseStrategy, useClass: LayoutRouteReuseStrategy },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
    MessageService,
    DialogService,
  ],
};
